import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { Check } from 'lucide-react';
import { api } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ErrorNote, Field } from '../components/kit';

interface Invitation {
  id: string;
  email: string;
  role: string | null;
  status: string;
  tenant: string;
  hasAccount: boolean;
  expired: boolean;
}

/**
 * The one page somebody can reach before they have an account. An invited
 * address with no account sets a password here — that is how accounts are made,
 * because there is no self-service sign-up anywhere else.
 */
export function AcceptPage({ signedInAs }: { signedInAs?: string }) {
  const { id = '' } = useParams();
  const invitation = useQuery({
    queryKey: ['invitation', id],
    queryFn: () => api.get<Invitation>(`/api/invitations/${id}`),
    retry: false,
  });

  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const accept = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; signedIn: boolean }>(`/api/invitations/${id}/accept`, {
        password: password || undefined,
        name: name || undefined,
      }),
    onSuccess: () => {
      // A fresh account is signed in by the accept itself; reload into the app.
      window.location.href = '/';
    },
  });

  const detail = invitation.data;
  const wrongAccount = Boolean(detail && signedInAs && signedInAs.toLowerCase() !== detail.email.toLowerCase());

  return (
    <Shell>
      {invitation.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : invitation.isError || !detail ? (
        <Note title="This invitation no longer exists">
          Ask whoever invited you to send a new one — a link can only be used once.
        </Note>
      ) : detail.status !== 'pending' ? (
        <Note title="This invitation has already been used">
          If that was you, sign in instead. If it was not, tell whoever invited you.
        </Note>
      ) : detail.expired ? (
        <Note title="This invitation has expired">Ask whoever invited you to send a new one.</Note>
      ) : (
        <>
          <h1 className="text-[19px] font-semibold tracking-[-0.015em]">Join {detail.tenant}</h1>
          <p className="mt-1.5 flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
            Invited as <Badge variant="default">{detail.role ?? 'member'}</Badge> for
            <span className="data text-ink">{detail.email}</span>
          </p>
          <p className="mt-3 text-[12.5px] text-ink-2">
            Accepting grants your account this tenant's runs, repositories and credentials. taskpilot runs
            coding agents unattended against those repositories, and the console can start them.
          </p>

          {wrongAccount ? (
            <Note title={`You are signed in as ${signedInAs}`} className="mt-5">
              This invitation was sent to {detail.email}. Sign out and sign in as that address, or ask for an
              invitation to the account you are using.
            </Note>
          ) : detail.hasAccount && !signedInAs ? (
            <Note title="You already have an account" className="mt-5">
              Sign in as {detail.email}, then open this link again to accept.
            </Note>
          ) : detail.hasAccount ? (
            <div className="mt-5">
              <Button onClick={() => accept.mutate()} disabled={accept.isPending}>
                Accept invitation
              </Button>
              <ErrorNote error={accept.error} />
            </div>
          ) : (
            <form
              className="mt-5 grid gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                accept.mutate();
              }}
            >
              <p className="text-[12.5px] text-ink-2">
                You have no account yet. Choose a password and this link creates one for {detail.email}.
              </p>
              <Field label="Your name" htmlFor="accept-name" hint="How you appear to the rest of the tenant.">
                <Input
                  id="accept-name"
                  autoFocus
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={detail.email.split('@')[0]}
                />
              </Field>
              <Field label="Password" htmlFor="accept-password" hint="At least 12 characters.">
                <Input
                  id="accept-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>
              <ErrorNote error={accept.error} />
              <div>
                <Button type="submit" disabled={accept.isPending || password.length < 12}>
                  Create the account and accept
                </Button>
              </div>
            </form>
          )}

          {accept.isSuccess && (
            <p className="mt-4 flex items-center gap-2 text-[13px] text-ok">
              <Check className="size-4" aria-hidden /> Accepted — opening taskpilot.
            </p>
          )}
        </>
      )}
    </Shell>
  );
}

/** Standing on its own: this page is reachable without a session, so it carries
 *  the wordmark the app shell would otherwise provide. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-[30rem] flex-col justify-center px-6 py-10">
      <div className="mb-5 flex items-center gap-2.5">
        <span
          aria-hidden
          className="grid size-7 place-items-center rounded-[6px] bg-brand text-[12px] font-bold text-brand-ink"
        >
          tp
        </span>
        <span className="text-[15px] font-semibold tracking-[-0.015em]">taskpilot</span>
      </div>
      <section className="rounded-[7px] border border-rule bg-panel p-5 shadow-[var(--shadow-panel)]">
        {children}
      </section>
    </main>
  );
}

function Note({
  title,
  children,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-[5px] border border-rule bg-raised px-3.5 py-3 ${className}`}>
      <p className="text-[13px] font-medium">{title}</p>
      <p className="mt-1 text-[12.5px] text-ink-2">{children}</p>
    </div>
  );
}
